import React from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Place as PlaceIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ReportDialog from './ReportDialog';
import ModerateDialog from './ModerateDialog';
import { eventDayParts, formatEventDate } from '../../constants/community';
import ModeratorBadge from './ModeratorBadge';
import PhotoGallery from './PhotoGallery';

/**
 * The day, in a block, the way a calendar shows it.
 *
 * An agenda is read by date first — "is there anything this weekend" — and a
 * date buried in a sentence forces every row to be read in full before that
 * question can be answered.
 */
function DayBlock({ startsAt, muted }) {
  const parts = eventDayParts(startsAt);

  if (!parts) {
    return null;
  }

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 64,
        flexShrink: 0,
        textAlign: 'center',
        py: 1.25,
        borderRadius: '14px',
        bgcolor: muted ? 'surface.chip' : 'success.light',
      }}
    >
      <Typography
        variant="h4"
        component="span"
        sx={{ display: 'block', lineHeight: 1, color: muted ? 'text.disabled' : 'secondary.main' }}
      >
        {parts.day}
      </Typography>
      <Typography
        variant="overline"
        component="span"
        sx={{ display: 'block', fontSize: '0.78125rem', mt: 0.4, color: muted ? 'text.disabled' : '#6c7d5b' }}
      >
        {parts.month}
      </Typography>
    </Box>
  );
}

/**
 * One entry in the agenda: what is happening, when, where and who organises it.
 *
 * Who is coming is not the application's business — residents turn up or they
 * do not, and a counter that only knows about the ones who clicked was never
 * telling the truth about the turnout.
 */
function EventCard({ event, onDelete, onModerated }) {
  const { user } = useAuth();

  const isOrganizer = user?.username && user.username === event.organizer_username;
  const closed = event.has_finished;

  return (
    <Card sx={{ opacity: closed ? 0.75 : 1 }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', gap: 2.5 }}>
        <DayBlock startsAt={event.starts_at} muted={closed} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1, minWidth: 0 }}>
          {(event.neighborhood || closed || 'HIDDEN' === event.status) && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {event.neighborhood && (
                <Chip icon={<PlaceIcon />} label={event.neighborhood.name} size="small" />
              )}
              {closed && <Chip label="Terminé" size="small" />}
              {'HIDDEN' === event.status && (
                <Chip icon={<HiddenIcon />} label="Masqué par la modération" size="small" color="error" />
              )}
            </Stack>
          )}

          <Typography variant="h4" component="h3">
            {event.title}
          </Typography>

          <Stack spacing={0.75}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
              <ScheduleIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatEventDate(event.starts_at)}
              </Typography>
            </Stack>

            {event.location && (
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
                <PlaceIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                <Typography variant="body1">{event.location}</Typography>
              </Stack>
            )}
          </Stack>

          {event.description && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {event.description}
            </Typography>
          )}

          <PhotoGallery medias={event.medias} title={event.title} compact />

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              pt: 1.75,
              borderTop: '1px solid',
              borderColor: 'surface.rule',
            }}
          >
            <Typography variant="caption">
              Organisé par {event.organizer}
              <ModeratorBadge show={event.organizer_is_moderator} />
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {isOrganizer && !closed && (
              <Button size="small" color="error" variant="outlined" onClick={() => onDelete?.(event)}>
                Annuler l’événement
              </Button>
            )}

            <ModerateDialog
              targetType="EVENT"
              targetUuid={event.uuid}
              citySlug={event.city_slug}
              isOwn={Boolean(isOrganizer)}
              hidden={'HIDDEN' === event.status}
              onModerated={onModerated}
            />
            <ReportDialog targetType="EVENT" targetUuid={event.uuid} />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default EventCard;
